import { Box, Card, CardContent, Typography, Fade, Grid } from '@mui/material';
import dayjs from 'dayjs';
import type React from 'react';
import type { RateInputs, RateOutputs } from '../types/rates';
import InfoTooltip from './InfoTooltip';

interface RateOutputDisplayProps {
  rateOutputs: RateOutputs | null;
  currentInputs: RateInputs | null;
  error?: string | null;
  loading?: boolean;
  showDirtyMessage?: boolean;
}

const RateOutputDisplay: React.FC<RateOutputDisplayProps> = ({ 
  rateOutputs,
  currentInputs,
  error = null,
  loading = false,
  showDirtyMessage = false
}) => {
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Rate Results
          </Typography>
          <Box sx={{ borderBottom: '2px solid #E5E5E5', mb: 3 }} />
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            Getting rates...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Rate Results
          </Typography>
          <Box sx={{ borderBottom: '2px solid #E5E5E5', mb: 3 }} />
          <Typography color="error" sx={{ textAlign: 'center', py: 4 }}>
            Error: {error}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!rateOutputs) {
    const message = showDirtyMessage 
      ? "Click 'Lookup Rates' to see your effective AGILE rates"
      : "Complete the form and click 'Lookup Rates' to see your effective AGILE rates";
    
    return (
      <Fade in={true} timeout={600} key={showDirtyMessage ? 'dirty' : 'initial'}>
        <Card>
          <CardContent>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Rate Results
            </Typography>
            <Box sx={{ borderBottom: '2px solid #E5E5E5', mb: 3 }} />
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {message}
            </Typography>
          </CardContent>
        </Card>
      </Fade>
    );
  }

  if (!rateOutputs) return '';
  const { period, nextPeriod } = rateOutputs;
  if (!currentInputs) return '';
  const { investmentDate, deferralPeriod } = currentInputs;
  // const isNewInvestor = investorType === 'new';

  // Calculate start and endDate based on the logic provided
  const getStartDate = (): string => {
    return dayjs(period.effectiveFrom).format('DD/MM/YYYY');
  };

  const getEndDate = (): string => {
    if (nextPeriod) {
      // If there is a nextPeriod, endDate = nextPeriod.effectiveFrom minus 1 day
      return dayjs(nextPeriod.effectiveFrom).subtract(1, 'day').format('DD/MM/YYYY');
    } else {
      // If no nextPeriod, endDate = end of month of current period
      return dayjs(period.effectiveFrom).endOf('month').format('DD/MM/YYYY');
    }
  };

  const nextPeriodDateRange = nextPeriod ? `${dayjs(nextPeriod.effectiveFrom).format('DD/MM/YYYY')} to ${dayjs(nextPeriod.effectiveFrom).endOf('month').format('DD/MM/YYYY')}` : ''
  const periodDateRange = `${getStartDate()} to ${getEndDate()}`
  // Calculate nextPeriod rates if available
  // const getNextPeriodRates = () => {
  //   if (!hasNextPeriod || !rateApiResponse) return null;
    
  //   // Use the same logic as current period but with nextPeriod data
  //   const nextRateData = rateApiResponse.data.nextPeriod;
  //   const gender = rateOutputs?.baseRate ? (investmentDate ? 'Male' : 'Female') : 'Male'; // Simplified for now
    
  //   // For now, return the same rates - this would need proper calculation logic
  //   return {
  //     baseRate: rateOutputs?.baseRate || 0,
  //     escalatorRate: rateOutputs?.escalatorRate || 0,
  //     lifetimeIncomeRate: rateOutputs?.lifetimeIncomeRate || 0
  //   };
  // };

  // const nextPeriodRates = getNextPeriodRates();
  // const hasMultipleRateSets = rateSets.length > 1;

  return (
    <Fade in={true} timeout={600}>
      <Card sx={{ backgroundColor: '#f8f9fa' }}>
      <CardContent>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          Rate Results
        </Typography>
        <Box sx={{ borderTop: '2px solid #E5E5E5', pt:2, mb: 2 }} >
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              fontSize: '0.75rem',
              fontStyle: 'italic',
              opacity: 0.8,
              pb: 2
            }}
          >
            The Lifetime Income Rates shown are based on the information and elections you have provided here.
          </Typography>
        </Box>
        {/* AC02a - Single Rate Set Display */}
        <Box sx={{ p: 3, backgroundColor: '#e8eaf6', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Lifetime income rates effective from {periodDateRange}
          </Typography>

          {/* Rate Fields */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Investment Date */}
          {investmentDate && (
            <Grid container spacing={2} alignItems="center">
              <Grid size={6}>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Investment Date
                </Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', textAlign: 'right' }}>
                  {dayjs(investmentDate).format('DD/MM/YYYY')}
                </Typography>
              </Grid>
            </Grid>
          )}

          {/* AC03 - Base Rate */}
          <Grid container spacing={2} alignItems="center">
            <Grid size={6}>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Age Based Rate
              </Typography>
            </Grid>
            <Grid size={6}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', textAlign: 'right' }}>
                {period.baseRate.toFixed(2)}%
              </Typography>
            </Grid>
          </Grid>

          {/* AC04 - Escalator Rate */}
          <Grid container spacing={2} alignItems="center">
            <Grid size={6}>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Annual Income Escalator
              </Typography>
            </Grid>
            <Grid size={6}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', textAlign: 'right' }}>
                {period.escalatorRate.toFixed(2)}%
              </Typography>
            </Grid>
          </Grid>

          {/* AC05 & AC06 - Lifetime Income Rate with Tooltip */}
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'flex-end',
              backgroundColor: 'white',
              p: 2,
              borderRadius: 1,
              border: '2px solid',
              borderColor: 'primary.main'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Lifetime Income Rate
                </Typography>
                <InfoTooltip>
                  <p>The AGILE Lifetime Income Rate is made up of two components - Your Age-Based Rate plus the sum of the Annual Income Escalators accumulated for every complete year you wait to start your AGILE Lifetime Income.</p>
                  <p>The Lifetime Income Rate is used to determine an Investor's starting annual income, which is determined by multiplying an AGILE Investor's Investment Value on the Lifetime Income Commencement Date by their Lifetime Income Rate.</p>
                </InfoTooltip>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {period.lifetimeIncomeRate.toFixed(2)}%
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <Typography variant='caption'>
                {periodDateRange}
              </Typography>
              <Typography variant='caption'>
                {period.baseRate.toFixed(2)}% + ({deferralPeriod} * {period.escalatorRate.toFixed(2)}%) = {period.lifetimeIncomeRate.toFixed(2)}%
              </Typography>
            </Box>
          </Box>
          </Box>
        </Box>

        {/* AC08 - Second Output Set (if dual rate sets) */}
        {nextPeriod && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              {`Upcoming Rates available from ${nextPeriodDateRange}`}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Age Based Rate
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {nextPeriod.baseRate.toFixed(2)}%
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Annual Income Escalator
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {nextPeriod.escalatorRate.toFixed(2)}%
                </Typography>
              </Box>

              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  backgroundColor: 'white',
                  p: 2,
                  borderRadius: 1,
                  border: '2px solid',
                  borderColor: 'primary.main'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Lifetime Income Rate
                    </Typography>
                    <InfoTooltip>
                      <p>The AGILE Lifetime Income Rate is made up of two components - Your Age-Based Rate plus the sum of the Annual Income Escalators accumulated for every complete year you wait to start your AGILE Lifetime Income.</p>
                      <p>The Lifetime Income Rate is used to determine an Investor's starting annual income, which is determined by multiplying an AGILE Investor's Investment Value on the Lifetime Income Commencement Date by their Lifetime Income Rate.</p>
                    </InfoTooltip>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {nextPeriod.lifetimeIncomeRate.toFixed(2)}%
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant='caption'>
                    {nextPeriodDateRange}
                  </Typography>
                  <Typography variant='caption'>
                    {nextPeriod.baseRate.toFixed(2)}% + ({deferralPeriod} * {nextPeriod.escalatorRate.toFixed(2)}%) = {nextPeriod.lifetimeIncomeRate.toFixed(2)}%
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Disclaimer */}
        <Box sx={{ 
          mt: 4, 
          pt: 3, 
          borderTop: '1px solid #E5E5E5',
          textAlign: 'center'
        }}>
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              fontSize: '0.75rem',
              fontStyle: 'italic',
              opacity: 0.8
            }}
          >
            Rates are reviewed monthly.
          </Typography>
        </Box>
      </CardContent>
    </Card>
    </Fade>
  );
};

export default RateOutputDisplay;